import { blockHistory, unblockHistory } from 'lib/history';
import { Util } from 'reactstrap';
import { logEvent, CONTINUE, ONBOARDING, NEXT, PREVIOUS } from 'lib/analytics';
import elementReady from 'lib/elementReady';
import introJs from 'intro.js';

// Max time after which we will start introJs, regardless of if all elements attached.
const ELEMENT_WAIT_TIMEOUT_MS = 5000;

/**
 * Acts as the onboarding tour bootstrap once the queue is set.
 *  - Redirects to the appropriate page (when needed)
 *  - Configures the appropriate options for the given tour in the queue.
 *  - Prepares the `Promise`s for the elements in the required tour.
 *  - Adds a `LoadingSpinner` until elements are ready (or timeout).
 *
 * Usage:
 *   Always be sure to `setProps()` and `setTranslate()` during
 *  `componentDidMount()` and `componentDidUpdate()` respectively.
 *
 *  Always be sure to call `cleanUp()`.
 *
 * If this grows to be much more complex, perhaps consider splitting
 * out some logic into separate class(es).
 */
export default class OnboardingTourService {
  /**
   * @param {Function} setIsPageLoadingCallback Used to notify when the isPageLoading
   *  has changed state.
   */
  constructor(setIsPageLoadingCallback) {
    this.introJs = null;
    this.setIsPageLoading = setIsPageLoadingCallback;
  }

  /**
   * Quick and dirty solution to pull logic out of the component.
   *
   * @param {object} props Expects: {
   *  tourQueue: Array (of strings),
   *  clientCode: String,
   *  locale: String,
   *  push: Function (registered with dispatch),
   *  startTour: Function (registered with dispatch),
   *  endTour: Function (registered with dispatch),
   *  skipTour: Function (registered with dispatch),
   * }
   */
  setProps(props) {
    this.props = props;
    return this;
  }

  /**
   * @param {Function} t
   */
  setTranslate(t) {
    this.t = t;
    return this;
  }

  /**
   * Performs all the work to start a new tour.
   *
   * @param {object} tourOptions Form described in `baseOptions`.
   */
  startTour(tourOptions) {
    this._checkAndPerformRedirect(tourOptions);
    this._configureIntroJs(tourOptions);
    this._checkAndStartIntroJs(tourOptions);
    return this;
  }

  /**
   * Cleans up and sets service to starting state.
   */
  cleanUp() {
    if (this.introJs) {
      this.introJs.exit();
      this.introJs = null;
    }
    unblockHistory();
    return this;
  }

  /**
   * Checks if there is a redirect needed and performs it.
   *
   * @param {object} tourOptions Expects `{novusPagePath: string|boolean}`
   */
  _checkAndPerformRedirect(tourOptions) {
    const { locale, clientCode, push } = this.props;

    if (tourOptions.novusPagePath) {
      // Redirect if there is a link specified.
      push(`/${locale}/${clientCode}/${tourOptions.novusPagePath}`);
      this.setIsPageLoading(true);
    }

    // Block history only after we have redirected.
    const confirmationMessage = this.t(
      'Are you sure you want to leave this page?'
    );
    blockHistory(confirmationMessage, (message) => {
      // Use inbuilt dialogue as MVP for back-button presses.
      const isNavigateAway = window.confirm(message);
      if (isNavigateAway) {
        this.cleanUp();
      } else {
        // Page refreshes if staying, not trivial to change nor in scope, instead:
        // Disable callbacks before we exit + re-enter tour.
        this.introJs.oncomplete(() => false);
        this.introJs.onexit(() => false);
        this.cleanUp();
        this.startTour(tourOptions);
      }
      return isNavigateAway;
    });
  }

  /**
   * Checks if redirect happened and performs timeout to ensure animation completes.
   *
   * @param {object} tourOptions Form described in `baseOptions`.
   */
  _checkAndStartIntroJs(tourOptions) {
    if (tourOptions.novusPagePath) {
      // We delay it because some menu items need to animate first.
      setTimeout(
        () => this._waitForElementsThenStartIntroJs(tourOptions),
        Util.TransitionTimeouts.Collapse
      );
    } else {
      this._waitForElementsThenStartIntroJs(tourOptions);
    }
  }

  /**
   * Waits for elements to be present in DOM before starting.
   * Times-out after `ELEMENT_WAIT_TIMEOUT_MS` time has elapsed.
   *
   * @param {object} tourOptions Form described in `baseOptions`.
   */
  _waitForElementsThenStartIntroJs(tourOptions) {
    const elementsReady = tourOptions.steps.map((step) => {
      if (step.element) {
        // Returns promise that resolve either when it is found or times-out.
        return elementReady(step.element, {
          stopOnDomReady: false,
          timeout: ELEMENT_WAIT_TIMEOUT_MS
        });
      }
      // No element means no promise to be made.
      return false;
    });
    // If there are no elements this resolves immediately.
    Promise.all(elementsReady).then(() => {
      this.introJs.start();
      this.setIsPageLoading(false);
    });
  }

  /**
   * Initializes and configures the `this.introJs` object.
   *
   * @param {object} tourOptions Form described in `baseOptions`.
   */
  _configureIntroJs(tourOptions) {
    const { tourQueue } = this.props;

    if (tourQueue.length > 1) {
      tourOptions.doneLabel = tourOptions.novusNextPageLabel;
    }
    this.introJs = introJs();
    this.introJs.setOptions(tourOptions);
    this._configureIntroJsCompleteCallbacks();
    this._configureIntroJsChangeCallbacks(tourOptions);
  }

  /**
   * Creates our introJs complete/exit callbacks with the logic baked in.
   */
  _configureIntroJsCompleteCallbacks() {
    const { tourQueue, startTour, endTour, skipTour } = this.props;
    const remainingQueue = tourQueue.slice(1);
    // Used to ensure we fire callbacks only once.
    let isCurrentTourComplete = false;
    let referrer = '';
    const skipOnboarding = () => {
      if (!isCurrentTourComplete) {
        isCurrentTourComplete = true;
        referrer = this.introJs._introItems[this.introJs._currentStep]
          .novusTrackingId;
        this.cleanUp();
        return skipTour(referrer);
      }
    };
    const completeOnboarding = () => {
      isCurrentTourComplete = true;
      referrer = this.introJs._introItems[this.introJs._introItems.length - 1]
        .novusTrackingId;
      this.cleanUp();
      return endTour(referrer);
    };
    const proceedToNextTour = () => {
      logEvent(
        ONBOARDING,
        CONTINUE,
        this.introJs._introItems[this.introJs._introItems.length - 1]
          .novusTrackingId
      );
      isCurrentTourComplete = true;
      this.cleanUp();
      return startTour({ tourQueue: remainingQueue });
    };

    this.introJs.onexit(skipOnboarding);
    this.introJs.oncomplete(
      remainingQueue.length ? proceedToNextTour : completeOnboarding
    );
  }

  /**
   * Creates our introJs change callbacks with the logic baked in.
   *
   * @param {object} tourOptions Form described in `baseOptions`
   */
  _configureIntroJsChangeCallbacks(tourOptions) {
    // Keys will be indices but indices may have gaps.
    let elementCache = new Map();
    const track = () => {
      let action = '';
      let step = '';
      if (this.introJs._direction == 'forward') {
        if (this.introJs._currentStep >= 1) {
          action = NEXT;
          step =
            tourOptions.steps[this.introJs._currentStep - 1].novusTrackingId;
        } else {
          return;
        }
      } else if (this.introJs._direction == 'backward') {
        action = PREVIOUS;
        step = tourOptions.steps[this.introJs._currentStep + 1].novusTrackingId;
      }
      logEvent(ONBOARDING, action, step);
    };

    // We use some internal structures from introJs below, making this slightly fragile.
    // However, there's no other way to access the _currentStep or reset callbacks.
    this.introJs.onchange((targetElement) => {
      const currentStep = tourOptions.steps[this.introJs._currentStep];
      if (currentStep.element && !elementCache.get(this.introJs._currentStep)) {
        elementCache.set(this.introJs._currentStep, targetElement);
      }
      track();
      if (currentStep.novusOnChange) {
        currentStep.novusOnChange(targetElement);
      }
    });
    /**
     * Performs the `novusOnReadyForNext()` action for the previous step as
     * a way to determine to continue to the current step/exit.
     *
     * @param {Integer} currentStepIndex
     * @param {Element} nextTarget Optional.
     * @return {boolean} True to continue, false to stay.
     */
    const checkOnReadyForNext = (currentStepIndex, nextTarget) => {
      const prevStepIndex = currentStepIndex - 1;
      const previousStep = tourOptions.steps[prevStepIndex];
      const isReady =
        previousStep && previousStep.novusOnReadyForNext
          ? previousStep.novusOnReadyForNext(
              elementCache ? elementCache.get(prevStepIndex) : undefined,
              nextTarget
            )
          : true;
      this._setTourErrorClass(!isReady);
      return isReady;
    };

    const onbeforechange = (targetElement) =>
      checkOnReadyForNext(this.introJs._currentStep, targetElement);
    this.introJs.onbeforechange(onbeforechange);

    this.introJs.onbeforeexit(() => {
      const isReady = checkOnReadyForNext(tourOptions.steps.length);
      if (isReady && elementCache) {
        // Intro.js WILL delete the callbacks + elementCache when it exits
        // but, just in case, check it to avoid memory leaks.
        elementCache.clear();
        elementCache = null;
      }
      return isReady;
    });

    this.introJs.onafterchange(() => {
      // We need to unset the callback because there's some funky introJs code that
      // if you use the keyboard + onbeforechange it will break in an unrecoverable
      // way. This is due to the library not checking if step[length + 1] is valid
      // when a callback is defined.
      // There is no other way to unset the callback at writing.
      if (this.introJs._currentStep >= tourOptions.steps.length - 1) {
        // By this point the next "onbeforechange" will be handled by "onbeforeexit"
        this.introJs._introBeforeChangeCallback = undefined;
      } else if (!this.introJs._introBeforeChangeCallback) {
        // Restore callback if going backwards.
        this.introJs.onbeforechange(onbeforechange);
      }
      return true;
    });
  }

  /**
   * Sets the error class on the tour view port.
   *
   * @param {boolean} isError
   */
  _setTourErrorClass(isError) {
    const errorCssClass = 'introjs-tour-error';
    const helperLayer = document.getElementsByClassName(
      'introjs-helperLayer'
    )[0];
    if (helperLayer) {
      helperLayer.classList.remove(errorCssClass);
      if (isError) {
        // We need the timeout for the browser to visually register the change.
        const arbitraryTimeout = 50;
        setTimeout(
          () => helperLayer.classList.add(errorCssClass),
          arbitraryTimeout
        );
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/common/OnboardingTour/OnboardingTourService.js